import axios from '../../axios';

// --- Wine basket persistence ----------------------------------------------
// The basket lives in Vuex (in-memory) so it survives in-app navigation, but is
// lost on a hard reload. We mirror it to localStorage keyed by the villa booking
// item id so it can be restored after a reload - strictly scoped to the villa it
// was saved against, and only for a limited window (matching the 12h villa cookie).
const BASKET_KEY_PREFIX = 'wine_basket_';
const BASKET_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours, matching the villa cookie

function basketStorageKey(villaBookingItemId) {
  return `${BASKET_KEY_PREFIX}${villaBookingItemId}`;
}

// Resolve the villa booking item id from state, falling back to the cookie (the
// cookie is the source of truth that survives a reload before state is hydrated).
function resolveVillaBookingItemId(state) {
  if (state.currentVillaBookingItemId) return state.currentVillaBookingItemId;
  try {
    if ($cookies.isKey('current_Wine_Villa_Booking_ItemId')) {
      return $cookies.get('current_Wine_Villa_Booking_ItemId');
    }
  } catch (e) { /* cookies unavailable - ignore */ }
  return null;
}

function persistWineBasket(villaBookingItemId, items) {
  if (!villaBookingItemId) return;
  try {
    if (items && items.length > 0) {
      localStorage.setItem(
        basketStorageKey(villaBookingItemId),
        JSON.stringify({ savedAt: Date.now(), items }),
      );
    } else {
      localStorage.removeItem(basketStorageKey(villaBookingItemId));
    }
  } catch (e) { /* private mode / quota - fail silently */ }
}

function clearPersistedWineBasket(villaBookingItemId) {
  if (!villaBookingItemId) return;
  try {
    localStorage.removeItem(basketStorageKey(villaBookingItemId));
  } catch (e) { /* ignore */ }
}

// Read the persisted basket for a villa, discarding it if older than the TTL.
function readPersistedWineBasket(villaBookingItemId) {
  if (!villaBookingItemId) return [];
  try {
    const raw = localStorage.getItem(basketStorageKey(villaBookingItemId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return [];
    if (!parsed.savedAt || (Date.now() - parsed.savedAt) > BASKET_TTL_MS) {
      clearPersistedWineBasket(villaBookingItemId);
      return [];
    }
    return parsed.items;
  } catch (e) {
    return [];
  }
}
// ---------------------------------------------------------------------------

export default {
  namespaced: true,

  state: {
    // The villa booking item that Wine is currently being ordered to (created when Client/Guest selects to order wine)
    currentVillaBookingItemId: null,
    currentVillaBookingItem: null,
    villaBookingItem: null,
    // Current Wine order basket items (add/remove from here as client is adding/removing wine items from/to basket)
    currentWineBasketItems: [], // { wineId, name, quantity, lineTotal }
    currentDeliveryChargeThreshold: null,
    currentDeliveryChargeAmount: null,
    filteredWineList: [],
    sortBy: 1,
    temporaryBasket: [],
  },

  getters: {
    getSortBy: (state) => state.sortBy,
    getFilteredWineList: (state) => state.filteredWineList,
    currentVillaBookingItem: (state) => state.currentVillaBookingItem,
    getCurrentVillaBookingItem: (state) => state.villaBookingItem,
    currentWineBasketItems: (state) =>
      state.currentWineBasketItems,
    currentVillaBookingItemId: (state) => {
      if (state.currentVillaBookingItemId) {
        return state.currentVillaBookingItemId;
      } if ($cookies.isKey('current_Wine_Villa_Booking_ItemId')) {
        state.currentVillaBookingItemId = $cookies.get('current_Wine_Villa_Booking_ItemId');
        return state.currentVillaBookingItemId;
      }
      return null;
    },
    currentDeliveryChargeThreshold: (state) => state.currentDeliveryChargeThreshold,
    currentDeliveryChargeAmount: (state) => state.currentDeliveryChargeAmount,
    getTemporaryBasket: (state) => state.temporaryBasket,
  },

  mutations: {
    setSortBy(state, payload) {
      state.sortBy = payload;
    },
    setFilteredWineList(state, payload) {
      state.filteredWineList = payload;
    },
    setCurrentVillaBookingItemId(state, id) {
      if (id) {
        // check if the id is different from the current Villa Booking Item Id then clear basket items
        if (state.currentVillaBookingItemId && state.currentVillaBookingItemId != id) {
          state.currentWineBasketItems = [];
        } else if ($cookies.isKey('current_Wine_Villa_Booking_ItemId') && $cookies.get('current_Wine_Villa_Booking_ItemId') != id) {
          state.currentWineBasketItems = [];
        }

        state.currentVillaBookingItemId = id;
        $cookies.set('current_Wine_Villa_Booking_ItemId', id, '12h', null, null, false); // expire after 12 hours
      } else {
        state.currentVillaBookingItemId = null;
        $cookies.remove('current_Wine_Villa_Booking_ItemId');
      }
    },
    setCurrentVillaBookingItem(state, item) {
      state.currentVillaBookingItem = item;
      state.villaBookingItem = item;
    },
    setCurrentWineBasketItems(state, items) {
      state.currentWineBasketItems = items;
      persistWineBasket(resolveVillaBookingItemId(state), items);
    },
    setCurrentDeliveryChargeThreshold(state, threshold) {
      state.currentDeliveryChargeThreshold = threshold;
    },
    setCurrentDeliveryChargeAmount(state, amount) {
      state.currentDeliveryChargeAmount = amount;
    },
    setTemporaryBasket(state, payload) {
      state.temporaryBasket = payload;
    },
  },

  actions: {

    fetchCurrentVillaBookingItem({ getters, commit }) {
      return new Promise((resolve, reject) => {
        if (getters.currentVillaBookingItem && getters.currentVillaBookingItem.id === getters.currentVillaBookingItemId) {
          // still in state and Id is still the same as the Current Cookie
          commit('setCurrentVillaBookingItem', getters.currentVillaBookingItem);
          resolve(getters.currentVillaBookingItem);
        } else if (getters.currentVillaBookingItemId) {
          const odata = '$select=*&$expand=property';

          // fetch from API and set
          axios.get(`/core/bookingitems/${getters.currentVillaBookingItemId}?${odata}`).then((res) => {
            commit('setCurrentVillaBookingItem', res.data);
            resolve(res.data);
          }).catch((err) => { reject(err); });
        } else {
          commit('setCurrentVillaBookingItem', null);
          reject(null);
        }
      });
    },

    isWineSalesPossible({}, villaBookingItemId) {
      return new Promise((resolve, reject) => {
        if (villaBookingItemId && villaBookingItemId > 0) {
          axios.get(`/core/bookings/wine/isWineSalesPossible/villabookingitemid/${villaBookingItemId}`).then((res) => {
            resolve(res);
          }).catch((err) => { reject(err); });
        } else {
          reject(null);
        }
      });
    },

    getWineProductsByCurrentVillaBookingItemId({ getters, commit }) {
      return new Promise((resolve, reject) => {
        if (getters.currentVillaBookingItemId && getters.currentVillaBookingItemId > 0) {
          axios.get(`/core/wines/villabookingitemid/${getters.currentVillaBookingItemId}`).then((res) => {
            resolve(res);
          }).catch((err) => { reject(err); });
        } else {
          reject(null);
        }
      });
    },

    getWineProductsByBookingItemId({ commit, dispatch }, bookingItem) {
      commit('setCurrentVillaBookingItem', bookingItem);
      commit('setCurrentVillaBookingItemId', bookingItem.id);
      // Restore any basket previously saved for this villa (e.g. after a reload).
      dispatch('rehydrateWineBasket');
      return new Promise((resolve, reject) => {
        axios.get(`/core/wines/villabookingitemid/${bookingItem.id}`).then((res) => {
          resolve(res);
        }).catch((err) => { reject(err); });
      });
    },

    // Restore the basket persisted in localStorage for the current villa. Only runs
    // when nothing is held in memory (i.e. a fresh load, e.g. after a "Back to wine"
    // reload from the pay page), and is scoped to the villa the basket was saved
    // against so it never leaks across villas. Only the real wine lines are restored;
    // any delivery-charge line (wineId < 0) is recalculated server-side the next time
    // the basket is re-priced. Callers are responsible for re-pricing.
    rehydrateWineBasket({ getters, commit }) {
      return new Promise((resolve) => {
        const villaBookingItemId = getters.currentVillaBookingItemId;
        if (!villaBookingItemId
          || (getters.currentWineBasketItems && getters.currentWineBasketItems.length > 0)) {
          resolve(getters.currentWineBasketItems || []);
          return;
        }

        const items = readPersistedWineBasket(villaBookingItemId).filter((w) => w.wineId > 0);
        if (items.length === 0) {
          resolve([]);
          return;
        }

        commit('setCurrentWineBasketItems', items);
        resolve(items);
      });
    },

    // item = { wineId, name, quantity, lineTotal, numberofBottles }
    addItemToWineBasket({ state }, item) {
      // check if item by wineId already exists and then just update the quantity and LineTotal
      const wine = state.currentWineBasketItems.length > 0 ? state.currentWineBasketItems.find((w) => w.wineId === item.wineId) : null;
      if (wine) {
        wine.quantity += item.quantity;
        wine.lineTotal += item.lineTotal;
        wine.numberofBottles += item.numberofBottles;
        // wine.winetype = wine.winetype;
      } else {
        state.currentWineBasketItems.push(item);
      }
      persistWineBasket(resolveVillaBookingItemId(state), state.currentWineBasketItems);
    },

    // item = { wineId, name, quantity, lineTotal }
    removeItemFromWineBasket({ state }, item) {
      const indexToRemove = state.currentWineBasketItems.findIndex((w) => w.wineId === item.wineId);
      if (indexToRemove > -1) {
        state.currentWineBasketItems.splice(indexToRemove, 1);
      }
      persistWineBasket(resolveVillaBookingItemId(state), state.currentWineBasketItems);
    },

    getCurrentDeliveryChargeThreshold({ getters, commit }) {
      return new Promise((resolve, reject) => {
        if (getters.currentVillaBookingItemId && getters.currentVillaBookingItemId > 0) {
          axios.get(`/core/bookings/wine/getdeliverychargethreshold/villabookingitemid/${getters.currentVillaBookingItemId}`).then((res) => {
            commit('setCurrentDeliveryChargeThreshold', res.data.threshold);
            commit('setCurrentDeliveryChargeAmount', res.data.deliveryCharge);
            resolve(res);
          }).catch((err) => {
            commit('setCurrentDeliveryChargeThreshold', null);
            commit('setCurrentDeliveryChargeAmount', null);
            reject(err);
          });
        } else {
          commit('setCurrentDeliveryChargeThreshold', null);
          commit('setCurrentDeliveryChargeAmount', null);
          reject(null);
        }
      });
    },

    // will add the applicable wine charge item to out current wine basket based on bastet items and other confirmed wines to same villa booking item
    applyDeliveryChargeToCurrentWineBasket({ getters, commit }) {
      return new Promise((resolve, reject) => {
        /// check that current villa booking item (ordered to) and wine basket exists
        if (getters.currentVillaBookingItemId && getters.currentVillaBookingItemId > 0 &&
          getters.currentWineBasketItems && getters.currentWineBasketItems.length > 0) {
          axios.post(`/core/bookings/wine/applydeliverycharge/villabookingitemid/${getters.currentVillaBookingItemId}`, getters.currentWineBasketItems).then((res) => {
            commit('setCurrentWineBasketItems', res.data);
            resolve(res);
          }).catch((error) => { reject(error); });
        } else {
          commit('setCurrentWineBasketItems', null);
          reject(null);
        }
      });
    },

    createWineBookingFromCurrentBasket({ getters, commit, dispatch }, complimentary) {
      let url = `/core/bookings/wine/order/villabookingitemid/${getters.currentVillaBookingItemId}`;
      if (complimentary) {
        url = `/core/bookings/wine/order/complimentary/villabookingitemid/${getters.currentVillaBookingItemId}`;
      }
      return new Promise((resolve, reject) => {
        /// check that current villa booking item (ordered to) and wine basket exists
        if (getters.currentVillaBookingItemId && getters.currentVillaBookingItemId > 0 &&
          getters.currentWineBasketItems && getters.currentWineBasketItems.length > 0) {
          axios.post(url, getters.currentWineBasketItems).then((res) => {
            // NOTE: the basket is intentionally kept (in-memory + persisted) here.
            // The wine booking is created before payment, so the user can still hit
            // "Back to wine" from the pay page to review/edit their order. The basket
            // is only cleared once payment succeeds (see the clearWineBasket action).
            // fetch and update current booking (force API call)
            dispatch('bookings/selectBooking', res.data, { root: true }).then(() => {
              resolve(res);
            });
          }).catch((error) => { reject(error); });
        } else {
          commit('setCurrentWineBasketItems', null);
          reject(null);
        }
      });
    },

    // Clear the wine basket once an order has actually been paid for, removing it
    // from both memory and localStorage so the next visit starts fresh.
    clearWineBasket({ getters, commit }) {
      clearPersistedWineBasket(getters.currentVillaBookingItemId);
      commit('setCurrentWineBasketItems', []);
    },

  },
};
